"use client";

import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Loader2, Search, SearchX, X } from "lucide-react";

import type { AppLocale } from "@/core/i18n/config";
import { localizeHref } from "@/core/i18n/routing";
import { getHealerProfileSlug } from "@/features/healer-profile/domain/services/healer-slug";
import type { SeekerDashboardContent } from "@/features/seeker-dashboard/types/seeker-dashboard-content";
import { DashboardShell } from "@/shared/ui/dashboard-shell";
import { cn } from "@/shared/lib/utils";
import type { SearchHealerResult, SearchResultsResponse, SearchType } from "../../types";
import { useSearch } from "../hooks/use-search";
import {
  BlogResultCard,
  HealerResultCard,
  ResultCardSkeleton,
} from "../components/search-result-cards";

interface DashboardSearchPageProps {
  content: SeekerDashboardContent;
  initialData?: SearchResultsResponse | null;
  initialQuery?: string;
  initialType?: SearchType;
  locale: AppLocale;
}

const TYPE_TABS: { value: SearchType; en: string; hi: string }[] = [
  { value: "all", en: "All", hi: "सभी" },
  { value: "healers", en: "Healers", hi: "हीलर" },
  { value: "blogs", en: "Articles", hi: "लेख" },
];

const POPULAR_EN = ["Anxiety", "Trauma", "Couples therapy", "Burnout", "Mindfulness"];
const POPULAR_HI = ["चिंता", "आघात", "युगल थेरेपी", "बर्नआउट", "माइंडफुलनेस"];

const listMotion = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.06 } },
};

function getDashboardHealerHref(locale: AppLocale, healer: SearchHealerResult): string {
  return localizeHref(locale, `/dashboard/seeker/healers/${getHealerProfileSlug(healer)}`);
}

export function DashboardSearchPage({
  content,
  initialData = null,
  initialQuery = "",
  initialType = "all",
  locale,
}: DashboardSearchPageProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const isHindi = locale === "hi";

  const { query, setQuery, type, setType, data, loading, error, hasSearched, clear } = useSearch({
    initialQuery,
    initialType,
    initialData,
  });

  useEffect(() => {
    const trimmed = query.trim();
    const params = new URLSearchParams();
    if (trimmed) params.set("q", trimmed);
    if (type !== "all") params.set("type", type);
    const qs = params.toString();
    const url = localizeHref(locale, `/dashboard/seeker/search${qs ? `?${qs}` : ""}`);
    window.history.replaceState(null, "", url);
  }, [query, type, locale]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const healers = data?.results.healers?.items ?? [];
  const blogs = data?.results.blogs?.items ?? [];
  const totalHealers = data?.results.healers?.total ?? 0;
  const totalBlogs = data?.results.blogs?.total ?? 0;
  const totalResults = totalHealers + totalBlogs;
  const showHealers = type === "all" || type === "healers";
  const showBlogs = type === "all" || type === "blogs" || type === "content";
  const suggestedQuery = data?.suggestedQuery ?? data?.correctedQuery ?? null;

  return (
    <DashboardShell content={content}>
      <section className="mt-4 rounded-xl border border-white/65 bg-white/94 p-5 shadow-[0_24px_64px_rgba(15,23,42,0.06)] ring-1 ring-slate-200/60 sm:p-6">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-sm font-medium text-[#155A7D]">
              {isHindi ? "डैशबोर्ड खोज" : "Dashboard search"}
            </p>
            <h1 className="mt-1 text-2xl font-semibold tracking-tight text-slate-950">
              {isHindi ? "हीलर और लेख खोजें" : "Find healers and articles"}
            </h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
              {isHindi
                ? "विशेषज्ञता, स्थान या विषय के अनुसार खोजें। आप डैशबोर्ड छोड़े बिना हीलर देख और बुक कर सकते हैं।"
                : "Search by specialty, location, or topic. Healer profiles and booking stay inside your dashboard."}
            </p>
          </div>

          <div className="w-full lg:max-w-xl">
            <div className="group flex items-center gap-3 rounded-full border border-slate-200 bg-white px-5 py-3 shadow-[0_14px_36px_rgba(15,23,42,0.06)] transition focus-within:border-[#155A7D]/40">
              <Search className="h-5 w-5 shrink-0 text-slate-400" />
              <input
                ref={inputRef}
                aria-label={isHindi ? "खोजें" : "Search"}
                className="w-full bg-transparent text-base text-slate-900 placeholder:text-slate-400 focus:outline-none"
                inputMode="search"
                onChange={(event) => setQuery(event.target.value)}
                placeholder={
                  isHindi
                    ? "जैसे, चिंता, मुंबई में थेरेपिस्ट..."
                    : "Try anxiety, therapist in Mumbai..."
                }
                type="search"
                value={query}
              />
              {loading ? <Loader2 className="h-4 w-4 animate-spin text-[#155A7D]" /> : null}
              {query && !loading ? (
                <button
                  aria-label={isHindi ? "साफ़ करें" : "Clear"}
                  className="shrink-0 rounded-full p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                  onClick={() => {
                    clear();
                    inputRef.current?.focus();
                  }}
                  type="button"
                >
                  <X className="h-4 w-4" />
                </button>
              ) : null}
            </div>

            {!hasSearched && !query ? (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className="text-xs text-slate-400">{isHindi ? "लोकप्रिय:" : "Popular:"}</span>
                {(isHindi ? POPULAR_HI : POPULAR_EN).map((term) => (
                  <button
                    className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-600 transition hover:border-[#155A7D]/30 hover:text-[#155A7D]"
                    key={term}
                    onClick={() => setQuery(term)}
                    type="button"
                  >
                    {term}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      </section>

      <section className="mt-5 pb-8">
        {(hasSearched || loading) && (
          <div className="mb-5 flex flex-col gap-4 rounded-xl border border-white/65 bg-white/90 p-4 shadow-[0_18px_46px_rgba(15,23,42,0.05)] sm:flex-row sm:items-center sm:justify-between">
            <div className="inline-flex w-fit rounded-full border border-slate-200 bg-white p-1">
              {TYPE_TABS.map((tab) => (
                <button
                  className={cn(
                    "rounded-full px-4 py-1.5 text-sm font-medium transition-colors",
                    type === tab.value || (type === "content" && tab.value === "blogs")
                      ? "bg-[#155A7D] text-white shadow-sm"
                      : "text-slate-500 hover:text-slate-900",
                  )}
                  key={tab.value}
                  onClick={() => setType(tab.value)}
                  type="button"
                >
                  {isHindi ? tab.hi : tab.en}
                </button>
              ))}
            </div>
            {hasSearched && !loading && !error ? (
              <p className="text-sm font-light text-slate-500">
                {totalResults > 0 ? (
                  <>
                    <span className="font-semibold text-slate-900">{totalResults}</span>{" "}
                    {isHindi ? "परिणाम के लिए" : "results for"}{" "}
                    <span className="font-medium text-slate-700">{`"${data?.query ?? ""}"`}</span>
                  </>
                ) : (
                  <>{isHindi ? "कोई परिणाम नहीं" : "No results"}</>
                )}
              </p>
            ) : null}
          </div>
        )}

        {hasSearched && !loading && suggestedQuery && suggestedQuery !== data?.query ? (
          <p className="mb-5 rounded-xl bg-white/80 px-4 py-3 text-sm text-slate-500">
            {isHindi ? "क्या आपका मतलब था" : "Did you mean"}{" "}
            <button
              className="font-semibold text-[#155A7D] underline-offset-2 hover:underline"
              onClick={() => setQuery(suggestedQuery)}
              type="button"
            >
              {suggestedQuery}
            </button>
            ?
          </p>
        ) : null}

        {loading ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: 6 }).map((_, index) => (
              <ResultCardSkeleton key={index} />
            ))}
          </div>
        ) : null}

        {!loading && error ? (
          <div className="rounded-xl border border-dashed border-rose-200 bg-rose-50/80 py-16 text-center">
            <SearchX className="mx-auto mb-3 h-9 w-9 text-rose-400" />
            <p className="font-light text-slate-600">
              {isHindi
                ? "खोज पूरी नहीं हो सकी। कृपया पुनः प्रयास करें।"
                : "Something went wrong. Please try again."}
            </p>
          </div>
        ) : null}

        {!loading && !error && hasSearched ? (
          <div className="space-y-10">
            {showHealers && healers.length > 0 ? (
              <div>
                {type === "all" ? (
                  <h2 className="mb-4 text-xl font-semibold tracking-tight text-slate-950">
                    {isHindi ? "हीलर" : "Healers"}{" "}
                    <span className="text-sm font-normal text-slate-400">({totalHealers})</span>
                  </h2>
                ) : null}
                <motion.div
                  animate="show"
                  className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3"
                  initial="hidden"
                  variants={listMotion}
                >
                  {healers.map((healer) => (
                    <HealerResultCard
                      healer={healer}
                      href={getDashboardHealerHref(locale, healer)}
                      key={healer.id}
                      locale={locale}
                    />
                  ))}
                </motion.div>
              </div>
            ) : null}

            {showBlogs && blogs.length > 0 ? (
              <div>
                {type === "all" ? (
                  <h2 className="mb-4 text-xl font-semibold tracking-tight text-slate-950">
                    {isHindi ? "लेख" : "Articles"}{" "}
                    <span className="text-sm font-normal text-slate-400">({totalBlogs})</span>
                  </h2>
                ) : null}
                <motion.div
                  animate="show"
                  className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3"
                  initial="hidden"
                  variants={listMotion}
                >
                  {blogs.map((blog) => (
                    <BlogResultCard blog={blog} key={blog.id} locale={locale} />
                  ))}
                </motion.div>
              </div>
            ) : null}

            {totalResults === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-200 bg-white/72 py-20 text-center">
                <SearchX className="mx-auto mb-4 h-10 w-10 text-slate-400" />
                <p className="text-lg font-light text-slate-700">
                  {isHindi
                    ? `"${data?.query}" के लिए कुछ नहीं मिला`
                    : `No results for "${data?.query}"`}
                </p>
                <p className="mx-auto mt-2 max-w-md text-sm font-light text-slate-500">
                  {isHindi
                    ? "वर्तनी जाँचें या कोई और कीवर्ड आज़माएँ।"
                    : "Check your spelling or try a different keyword."}
                </p>
              </div>
            ) : null}
          </div>
        ) : null}

        {!loading && !hasSearched && !error ? (
          <div className="rounded-xl border border-dashed border-slate-200 bg-white/72 py-20 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#155A7D]/10">
              <Search className="h-6 w-6 text-[#155A7D]" />
            </div>
            <p className="text-lg font-light text-slate-600">
              {isHindi ? "शुरू करने के लिए ऊपर टाइप करें" : "Start typing to search"}
            </p>
          </div>
        ) : null}
      </section>
    </DashboardShell>
  );
}
